import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { brand } from '../portalTheme';
import { EmptyState, ErrorState, LoadingState, PageTitle, useAsync, usePortalContext } from '../PortalUi';
import { fetchNotifications, markAllNotificationsRead, markNotificationRead } from '../../../../services/request/CustomerPortal';
import { formatDateTime } from '../../../../utils/customerPortalFormat';
import { useTranslation } from '../../../../i18n';

export default function PortalNotifications() {
  const { refreshUnread } = usePortalContext();
  const { t, term } = useTranslation();
  const notifications = useAsync(fetchNotifications, []);
  const items = notifications.data ?? [];
  const hasUnread = items.some(item => !item.read);

  const markRead = async (id: string) => {
    try {
      await markNotificationRead(id);
    } finally {
      notifications.reload();
      refreshUnread();
    }
  };

  const markAll = async () => {
    try {
      await markAllNotificationsRead();
    } finally {
      notifications.reload();
      refreshUnread();
    }
  };

  return (
    <Box>
      <PageTitle
        kicker={t('portal.notifications.kicker')}
        title={t('portal.notifications.title')}
        subtitle={t('portal.notifications.subtitle')}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={notifications.reload}>{t('portal.notifications.refresh')}</Button>
            {hasUnread ? (
              <Button variant="contained" onClick={markAll}>
                {t('portal.notifications.markAll')}
              </Button>
            ) : null}
          </Box>
        }
      />
      {notifications.error ? <ErrorState message={notifications.error} onRetry={notifications.reload} /> : null}
      {notifications.loading && !notifications.data ? <LoadingState label={t('portal.notifications.loading')} /> : null}
      {notifications.data && items.length === 0 ? <EmptyState title={t('portal.notifications.empty')} /> : null}
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        {items.map(item => (
          <Paper
            key={item.id}
            component={item.read ? 'div' : 'button'}
            type={item.read ? undefined : 'button'}
            onClick={item.read ? undefined : () => markRead(item.id)}
            sx={{
              p: 2.5,
              textAlign: 'left',
              font: 'inherit',
              color: 'inherit',
              width: '100%',
              cursor: item.read ? 'default' : 'pointer',
              bgcolor: item.read ? brand.white : brand.goldSoft,
              borderLeft: `4px solid ${item.read ? brand.border : brand.gold}`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="overline" sx={{ color: brand.warning }}>
                {term('notificationType', item.type, t('portal.common.notice'))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(item.createdDate) ?? ''}
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 700 }}>{item.title}</Typography>
            {item.body ? (
              <Typography color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                {item.body}
              </Typography>
            ) : null}
            {!item.read ? (
              <Typography variant="caption" sx={{ color: brand.warning, mt: 1, display: 'block' }}>
                {t('portal.notifications.unreadHint')}
              </Typography>
            ) : null}
          </Paper>
        ))}
      </Box>
    </Box>
  );
}
